import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["children"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { ELEMENT_ITEM_HEIGHT, ElementBrowser } from '@atlaskit/editor-common/element-browser';
import { IconCode, IconDate, IconDecision, IconDivider, IconExpand, IconPanel, IconQuote, IconStatus } from '@atlaskit/editor-common/quick-insert';
import { withReactEditorViewOuterListeners as withOuterListeners } from '@atlaskit/editor-common/ui-react';
// AFP-2532 TODO: Fix automatic suppressions below
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
import { borderRadius } from '@atlaskit/theme';
import { N0, N30A, N60A } from '@atlaskit/theme/colors';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
var InsertMenu = function InsertMenu(_ref) {
  var _pluginInjectionApi$q7, _pluginInjectionApi$q8;
  var editorView = _ref.editorView,
    dropdownItems = _ref.dropdownItems,
    showElementBrowserLink = _ref.showElementBrowserLink,
    onInsert = _ref.onInsert,
    toggleVisiblity = _ref.toggleVisiblity,
    pluginInjectionApi = _ref.pluginInjectionApi;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    itemCount = _useState2[0],
    setItemCount = _useState2[1];
  var transform = useCallback(function (item) {
    return {
      title: item.content,
      description: item.tooltipDescription,
      keyshortcut: item.shortcut,
      icon: function icon() {
        return getSvgIconForItem({
          name: item.value.name
        }) || item.elemBefore;
      },
      /**
       * @note This transformed items action is only used when a quick insert item has been
       * called from the quick insert menu and a search has not been performed.
       */
      action: function action() {
        return onInsert({
          item: item
        });
      },
      // "insertInsertMenuItem" expects these 2 properties.
      onClick: item.onClick,
      value: item.value
    };
  }, [onInsert]);
  var quickInsertDropdownItems = dropdownItems.map(transform);
  var viewMoreItem = showElementBrowserLink ? quickInsertDropdownItems.pop() : undefined;
  var onInsertItem = useCallback(function (item) {
    toggleVisiblity();
    if (!editorView.hasFocus()) {
      editorView.focus();
    }
    if (editorExperiment('insert-menu-in-right-rail', true)) {
      var _pluginInjectionApi$q;
      pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q === void 0 || _pluginInjectionApi$q.actions.insertItem(item,
      // @ts-expect-error
      INPUT_METHOD.INSERT_MENU_RIGHT_RAIL)(editorView.state, editorView.dispatch);
    } else {
      var _pluginInjectionApi$q2;
      pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q2 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q2 === void 0 || _pluginInjectionApi$q2.actions.insertItem(item, INPUT_METHOD.TOOLBAR)(editorView.state, editorView.dispatch);
    }
  }, [editorView, toggleVisiblity, pluginInjectionApi]);
  var getItems = useCallback(function (query, category) {
    var result;
    /**
     * @warning The results if there is a query are not the same as the results if there is no query.
     * For example: If you have a typed panel and then select the panel item then it will call a different action
     * than is specified on the editor plugins quick insert
     * @see above transform function for more details.
     */
    if (query) {
      var _pluginInjectionApi$q3, _pluginInjectionApi$q4;
      result = (_pluginInjectionApi$q3 = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q4 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q4 === void 0 ? void 0 : _pluginInjectionApi$q4.actions.getSuggestions({
        query: query,
        category: category
      })) !== null && _pluginInjectionApi$q3 !== void 0 ? _pluginInjectionApi$q3 : [];
    } else {
      var _pluginInjectionApi$q5, _pluginInjectionApi$q6;
      var featuredQuickInsertSuggestions = (_pluginInjectionApi$q5 = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q6 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q6 === void 0 ? void 0 : _pluginInjectionApi$q6.actions.getSuggestions({
        category: category,
        featuredItems: true,
        // @ts-ignore
        templateItems: editorExperiment('element-level-templates', true)
      })) !== null && _pluginInjectionApi$q5 !== void 0 ? _pluginInjectionApi$q5 : [];
      if (editorExperiment('element-level-templates', true)) {
        // Make sure template options appear as top 5 items
        featuredQuickInsertSuggestions.sort(function (a, b) {
          var _b$priority, _a$priority;
          return ((_b$priority = b.priority) !== null && _b$priority !== void 0 ? _b$priority : 0) - ((_a$priority = a.priority) !== null && _a$priority !== void 0 ? _a$priority : 0);
        });
        var templateItems = featuredQuickInsertSuggestions.splice(0, 5);
        result = [].concat(_toConsumableArray(templateItems), _toConsumableArray(quickInsertDropdownItems), _toConsumableArray(featuredQuickInsertSuggestions));
      } else {
        result = quickInsertDropdownItems.concat(featuredQuickInsertSuggestions);
      }
    }
    setItemCount(result.length);
    return result;
  }, [pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q7 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q7 === void 0 ? void 0 : _pluginInjectionApi$q7.actions, quickInsertDropdownItems]);
  var emptyStateHandler = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q8 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q8 === void 0 || (_pluginInjectionApi$q8 = _pluginInjectionApi$q8.sharedState.currentState()) === null || _pluginInjectionApi$q8 === void 0 ? void 0 : _pluginInjectionApi$q8.emptyStateHandler;
  return (
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    jsx("div", {
      css: insertMenuWrapper(itemCount)
    }, jsx(ElementBrowserWrapper, {
      handleClickOutside: toggleVisiblity,
      handleEscapeKeydown: toggleVisiblity,
      closeOnTab: true
    }, jsx(ElementBrowser, {
      mode: "inline",
      getItems: getItems,
      emptyStateHandler: emptyStateHandler,
      onInsertItem: onInsertItem,
      showSearch: true,
      showCategories: false
      // On page resize we want the InlineElementBrowser to show updated tools/overflow items
      ,
      key: quickInsertDropdownItems.length,
      viewMoreItem: viewMoreItem
    })))
  );
};
var getSvgIconForItem = function getSvgIconForItem(_ref2) {
  var name = _ref2.name;
  var Icon = {
    codeblock: IconCode,
    panel: IconPanel,
    blockquote: IconQuote,
    decision: IconDecision,
    horizontalrule: IconDivider,
    expand: IconExpand,
    date: IconDate,
    status: IconStatus
  }[name];
  return Icon ? jsx(Icon, {
    label: ""
  }) : undefined;
};
var getInsertMenuHeight = function getInsertMenuHeight(_ref3) {
  var itemCount = _ref3.itemCount;
  // Figure based on visuals to exclude the searchbar, padding/margin, and the ViewMore item.
  var EXTRA_SPACE_EXCLUDING_ELEMENTLIST = 128;
  if (itemCount > 0 && itemCount < 6) {
    return itemCount * ELEMENT_ITEM_HEIGHT + EXTRA_SPACE_EXCLUDING_ELEMENTLIST;
  }
  return 560; // For showing 6 Elements.
};
var insertMenuWrapper = function insertMenuWrapper(itemCount) {
  if (editorExperiment('insert-menu-in-right-rail', true)) {
    return css({
      display: 'flex',
      flexDirection: 'column',
      width: '310px',
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      height: 'calc(100% - 32px)',
      margin: "0 -10px",
      backgroundColor: "".concat("var(--ds-surface-overlay, ".concat(N0, ")")),
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      borderRadius: "".concat(borderRadius(), "px")
    });
  }
  return css({
    display: 'flex',
    flexDirection: 'column',
    width: '320px',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    height: "".concat(getInsertMenuHeight({
      itemCount: itemCount
    }), "px"),
    backgroundColor: "".concat("var(--ds-surface-overlay, ".concat(N0, ")")),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    borderRadius: "".concat(borderRadius(), "px"),
    boxShadow: "".concat("var(--ds-shadow-overlay, ".concat("0 0 0 1px ".concat(N30A, ",\n    0 2px 1px ").concat(N30A, ",\n    0 0 20px -6px ").concat(N60A), ")"))
  });
};
var flexWrapperStyles = css({
  display: 'flex',
  flex: 1,
  boxSizing: 'border-box',
  overflow: 'hidden'
});
var FlexWrapper = function FlexWrapper(props) {
  var children = props.children,
    divProps = _objectWithoutProperties(props, _excluded);
  return jsx("div", _extends({
    css: flexWrapperStyles
  }, divProps), children);
};
var ElementBrowserWrapper = withOuterListeners(FlexWrapper);
export default InsertMenu;